/**
 * Filter mega panel (desktop, horizontal filters).
 *
 * The Filters button in the horizontal filter bar opens one panel
 * beneath the bar with every filter laid out side by side, each under its own
 * heading. The panel sits in the page flow, so it pushes the products down
 * rather than covering them. Clicking Filters again, clicking outside
 * the filters, or pressing Escape closes it.
 *
 * Each filter's inputs are moved into the panel while it is open and moved
 * back when it closes. They stay inside the filter form, so filtering works
 * exactly as before and choices apply straight away. Horizon re-renders the
 * filters after each change, so the panel is rebuilt whenever that happens
 * while it is open.
 *
 * Mobile keeps the filter drawer; nothing here runs below 750px.
 */

const DESKTOP = window.matchMedia('(min-width: 750px)');
const ROOT = '.facets.facets--horizontal:not(.facets-controls-wrapper)';
const PANEL = 'details.facets__panel';

let isOpen = false;
let frame = 0;
let building = false;

// Where each filter's options came from, so they can be put back on close.
// Kept by reference, as the price filter has no id to look it up by.
const homes = new WeakMap();

const getRoot = () => document.querySelector(ROOT);

function getPanels(root) {
  if (!root) return [];
  // Only the filters themselves; Sort shares the same drop-down markup but
  // keeps its own drop-down. Filters Horizon has tucked into its "More" list
  // are included too: the bar is hidden behind the Filters button, so Horizon
  // moves every filter after the first two into that list.
  return [...root.querySelectorAll(`.facets__form .facets__filters-wrapper ${PANEL}`)].filter(
    (details) => !details.closest('.sorting-filter, sorting-filter-component')
  );
}

function build() {
  const root = getRoot();
  const form = root?.querySelector('.facets__form');
  if (!root || !form) return;

  building = true;
  root.classList.add('facets--mega-open');

  root.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'true');

  // Close any other open drop-down in the bar, such as Sort.
  root.querySelectorAll('details[open]').forEach((details) => {
    details.open = false;
  });

  let mega = form.querySelector(':scope > .mega-filters');
  if (!mega) {
    mega = document.createElement('div');
    mega.className = 'mega-filters';
    form.appendChild(mega);
  }

  if (!mega.querySelector(':scope > .mega-filters__close')) {
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'button-unstyled mega-filters__close';
    closeButton.setAttribute('aria-label', 'Close filters');
    closeButton.innerHTML =
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      close();
    });
    mega.prepend(closeButton);
  }

  if (!mega.querySelector(':scope > .mega-filters__apply')) {
    const applyButton = document.createElement('button');
    applyButton.type = 'button';
    applyButton.className = 'mega-filters__apply';
    applyButton.textContent = 'Apply';
    applyButton.addEventListener('click', (event) => {
      event.preventDefault();
      close();
    });
    mega.appendChild(applyButton);
  }

  getPanels(root).forEach((details, index) => {
    details.classList.add('facets__panel--mega');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');

    const content = details.querySelector(':scope > .facets__panel-content, :scope .facets__panel-content');
    if (!(content instanceof HTMLElement) || content.closest('.mega-filters')) return;

    const group = document.createElement('div');
    group.className = 'mega-filters__group';
    group.dataset.megaIndex = String(index);

    const heading = document.createElement('p');
    heading.className = 'mega-filters__heading';
    heading.textContent = content.dataset.megaLabel || details.querySelector('summary')?.textContent?.trim() || '';

    homes.set(content, details);

    // Panel order: Gender, then Sizes, then Colour, then Price.
    const label = heading.textContent.toLowerCase();
    if (content.querySelector('.price-facet__slider') || label.includes('price')) group.style.order = '4';
    else if (/colou?r/.test(label)) group.style.order = '3';
    else if (label.includes('gender')) {
      group.style.order = '1';
      group.classList.add('mega-filters__group--stacked');
    }
    else group.style.order = '2';

    group.append(heading, content);
    mega.appendChild(group);

    // Two-column lists, such as Sizes, read down each column in the order set
    // in Search & Discovery, rather than across the rows.
    if (!group.classList.contains('mega-filters__group--stacked')) {
      content.querySelectorAll('.facets__inputs-list:not(.facets__inputs-list--swatches)').forEach((list) => {
        const count = list.querySelectorAll(':scope > li').length;
        list.style.gridAutoFlow = 'column';
        list.style.gridTemplateRows = `repeat(${Math.max(1, Math.ceil(count / 2))}, auto)`;
      });
    }
  });

  building = false;
}

function open() {
  isOpen = true;
  build();
}

function close() {
  isOpen = false;
  const root = getRoot();
  if (!root) return;

  building = true;
  root.classList.remove('facets--mega-open');
  root.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'false');

  root.querySelectorAll('.mega-filters .facets__panel-content').forEach((content) => {
    const home = homes.get(content);
    if (home && home.isConnected) home.appendChild(content);
  });
  root.querySelectorAll('.mega-filters').forEach((mega) => mega.remove());

  getPanels(root).forEach((details) => {
    details.classList.remove('facets__panel--mega');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });

  building = false;
}

if (!window.__pehrMegaFilters) {
  window.__pehrMegaFilters = true;

  // Apply and the X close the panel on the first press. Handled here, on
  // press rather than click, because Horizon can redraw the panel between
  // pressing and releasing just after a filter is ticked, which loses the
  // click.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!isOpen || !(event.target instanceof Element)) return;
      if (event.target.closest('.mega-filters__apply, .mega-filters__close')) {
        event.preventDefault();
        close();
      }
    },
    true
  );

  // The Filters button opens and closes the panel.
  document.addEventListener(
    'click',
    (event) => {
      if (!DESKTOP.matches || !(event.target instanceof Element)) return;
      const toggle = event.target.closest('[data-mega-toggle]');
      if (!toggle) return;
      event.preventDefault();
      if (isOpen) close();
      else open();
    },
    true
  );

  // Filter names, if shown, open and close the panel instead of their own
  // drop-downs.
  document.addEventListener(
    'click',
    (event) => {
      if (!DESKTOP.matches || !(event.target instanceof Element)) return;
      const root = getRoot();
      const summary = event.target.closest('summary');
      if (!root || !summary || !root.contains(summary)) return;
      const details = summary.closest(PANEL);
      if (!details || !getPanels(root).includes(details)) {
        // Another drop-down in the bar, such as Sort, closes the panel.
        if (isOpen) close();
        return;
      }

      event.preventDefault();
      if (isOpen) close();
      else open();
    },
    true
  );

  // A click anywhere outside the filters closes the panel.
  document.addEventListener('click', (event) => {
    if (!isOpen || !(event.target instanceof Node)) return;
    const root = getRoot();
    if (root && !root.contains(event.target)) close();
  });

  document.addEventListener('keydown', (event) => {
    if (isOpen && event.key === 'Escape') close();
  });

  // Horizon replaces the filter markup after each change; rebuild the panel.
  new MutationObserver(() => {
    if (!isOpen || building || !DESKTOP.matches) return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const root = getRoot();
      const stillBuilt = root?.querySelector('.facets__form > .mega-filters .facets__panel-content');
      const looseContent = getPanels(root).some((d) => d.querySelector('.facets__panel-content'));
      if (!stillBuilt || looseContent) build();
    });
  }).observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', () => {
    if (isOpen && !DESKTOP.matches) close();
  });
}

